"use client";

import React, { createContext, useContext, useState, ReactNode } from "react";
import { BusinessItem } from "@/core/models/businesses/businessList";
import { toast } from "react-toastify";

interface BusinessCompareContextType {
    compareBusinesses: BusinessItem[];
    addToCompare: (business: BusinessItem) => void;
    removeFromCompare: (companyId: number) => void;
    clearCompare: () => void;
    isInCompare: (companyId: number) => boolean;
    isCompareOpen: boolean;
    setCompareOpen: (open: boolean) => void;
}

const BusinessCompareContext = createContext<BusinessCompareContextType | undefined>(undefined);

export const useBusinessCompare = () => {
    const context = useContext(BusinessCompareContext);
    if (!context) {
        throw new Error("useBusinessCompare must be used within a BusinessCompareProvider");
    }
    return context;
};

interface BusinessCompareProviderProps {
    children: ReactNode;
}

export const BusinessCompareProvider: React.FC<BusinessCompareProviderProps> = ({ children }) => {
    const [compareBusinesses, setCompareBusinesses] = useState<BusinessItem[]>([]);
    const [isCompareOpen, setCompareOpen] = useState(false);

    const addToCompare = (business: BusinessItem) => {
        // Check conditions before updating state
        if (compareBusinesses.length >= 2) {
            toast.warning("Maximum 2 businesses can be compared");
            return;
        }
        if (compareBusinesses.some((item) => item.companyId === business.companyId)) {
            return;
        }
        
        // Only update state and show toast if checks pass
        setCompareBusinesses((prev) => [...prev, business]);
        toast.success("Added to comparison");
    };

    const removeFromCompare = (companyId: number) => {
        // Only show toast if business actually exists in compare list
        if (compareBusinesses.some((item) => item.companyId === companyId)) {
            setCompareBusinesses((prev) => prev.filter((item) => item.companyId !== companyId));
            toast.info("Removed from comparison");
        }
    };

    const clearCompare = () => {
        setCompareBusinesses([]);
        setCompareOpen(false);
    };

    const isInCompare = (companyId: number) => {
        return compareBusinesses.some((item) => item.companyId === companyId);
    };

    return (
        <BusinessCompareContext.Provider
            value={{
                compareBusinesses,
                addToCompare,
                removeFromCompare,
                clearCompare,
                isInCompare,
                isCompareOpen,
                setCompareOpen,
            }}
        >
            {children}
        </BusinessCompareContext.Provider>
    );
};
